import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";
import api from "../lib/api";

const ShopContext = createContext(null);
const LS_KEY = "cf_basket";
const LS_VOUCHER = "cf_voucher";

const readLocal = () => { try { return JSON.parse(localStorage.getItem(LS_KEY) || "[]"); } catch { return []; } };
const writeLocal = (items) => localStorage.setItem(LS_KEY, JSON.stringify(items));
const readVoucher = () => localStorage.getItem(LS_VOUCHER) || null;
const writeVoucher = (code) => code ? localStorage.setItem(LS_VOUCHER, code) : localStorage.removeItem(LS_VOUCHER);

function baseLocal(items) {
  const list = items.map((i) => ({ ...i, line_total: +(i.retail_price * i.qty).toFixed(2), in_stock: true }));
  const sub = +list.reduce((a, i) => a + i.line_total, 0).toFixed(2);
  return { items: list, count: list.reduce((a, i) => a + i.qty, 0), subtotal: sub, issues: [], discounts: [], total_savings: 0, rrp_total: sub, rrp_savings: 0, combined_savings: 0, estimated_total: sub };
}

export function ShopProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [basket, setBasket] = useState({ items: [], count: 0, subtotal: 0, issues: [], discounts: [], total_savings: 0, estimated_total: 0 });
  const [favIds, setFavIds] = useState([]);
  const merged = useRef(false);

  // For anonymous baskets, ask the backend (server-authoritative) for discounts.
  const computeAnon = useCallback(async (items) => {
    const local = baseLocal(items);
    const code = readVoucher();
    try {
      const { data } = await api.post("/promotions/calculate", { items: items.map((i) => ({ product_id: i.product_id, qty: i.qty })), voucher_code: code });
      return { ...local, subtotal: data.subtotal, rrp_total: data.rrp_total, rrp_savings: data.rrp_savings, discounts: data.discounts, promotion_messages: data.promotion_messages, total_savings: data.total_savings, combined_savings: data.combined_savings, estimated_total: data.estimated_total, voucher_code: data.voucher_code, voucher_valid: data.voucher_valid, voucher_message: data.voucher_message };
    } catch { return local; }
  }, []);

  const refreshBasket = useCallback(async () => {
    if (isAuthenticated) {
      const { data } = await api.get("/me/basket");
      setBasket(data);
    } else {
      setBasket(await computeAnon(readLocal()));
    }
  }, [isAuthenticated, computeAnon]);

  const refreshFavs = useCallback(async () => {
    if (isAuthenticated) {
      try { const { data } = await api.get("/me/favourite-ids"); setFavIds(data); } catch { setFavIds([]); }
    } else setFavIds([]);
  }, [isAuthenticated]);

  // On login: merge any anonymous basket into the server basket, then load.
  useEffect(() => {
    (async () => {
      if (isAuthenticated && !merged.current) {
        merged.current = true;
        const local = readLocal();
        if (local.length) {
          try { await api.post("/me/basket/merge", { items: local.map((i) => ({ product_id: i.product_id, qty: i.qty })) }); } catch { /* ignore */ }
          writeLocal([]);
        }
        const lv = readVoucher();
        if (lv) { try { await api.post("/me/basket/voucher", { code: lv }); } catch { /* ignore */ } writeVoucher(null); }
      }
      if (!isAuthenticated) merged.current = false;
      await refreshBasket();
      await refreshFavs();
    })();
  }, [isAuthenticated, refreshBasket, refreshFavs]);

  const addToBasket = useCallback(async (product, qty = 1) => {
    if (product.stock_status !== "in_stock") { toast.error("This product isn't available to reserve yet."); return; }
    if (isAuthenticated) {
      const { data } = await api.post("/me/basket/items", { product_id: product.product_id, qty });
      setBasket(data);
    } else {
      const items = readLocal();
      const ex = items.find((i) => i.product_id === product.product_id);
      const img = product.image || product.primary_image || (product.images?.[0]?.webp_url) || (product.images?.[0]?.url);
      if (ex) ex.qty = Math.min(99, ex.qty + qty);
      else items.push({ product_id: product.product_id, qty, name: product.name, slug: product.slug, retail_price: product.retail_price, image: img });
      writeLocal(items); setBasket(await computeAnon(items));
    }
    toast.success("Added to basket");
  }, [isAuthenticated, computeAnon]);

  const updateQty = useCallback(async (product_id, qty) => {
    if (isAuthenticated) {
      const { data } = await api.patch(`/me/basket/items/${product_id}`, { qty });
      setBasket(data);
    } else {
      let items = readLocal();
      if (qty <= 0) items = items.filter((i) => i.product_id !== product_id);
      else items = items.map((i) => (i.product_id === product_id ? { ...i, qty: Math.min(99, qty) } : i));
      writeLocal(items); setBasket(await computeAnon(items));
    }
  }, [isAuthenticated, computeAnon]);

  const removeItem = useCallback((product_id) => updateQty(product_id, 0), [updateQty]);

  const clearBasket = useCallback(async () => {
    if (isAuthenticated) { const { data } = await api.delete("/me/basket"); setBasket(data); }
    else { writeLocal([]); writeVoucher(null); setBasket(await computeAnon([])); }
  }, [isAuthenticated, computeAnon]);

  const applyVoucher = useCallback(async (code) => {
    if (isAuthenticated) {
      try { const { data } = await api.post("/me/basket/voucher", { code }); setBasket(data); return { ok: true, message: data.voucher_message }; }
      catch (e) { return { ok: false, message: e?.response?.data?.detail || "Coupon could not be applied." }; }
    }
    writeVoucher(code);
    const shaped = await computeAnon(readLocal());
    setBasket(shaped);
    if (shaped.voucher_valid) return { ok: true, message: shaped.voucher_message };
    writeVoucher(null);
    return { ok: false, message: shaped.voucher_message || "Coupon could not be applied." };
  }, [isAuthenticated, computeAnon]);

  const removeVoucher = useCallback(async () => {
    if (isAuthenticated) { const { data } = await api.delete("/me/basket/voucher"); setBasket(data); }
    else { writeVoucher(null); setBasket(await computeAnon(readLocal())); }
  }, [isAuthenticated, computeAnon]);

  const isFav = useCallback((pid) => favIds.includes(pid), [favIds]);

  const toggleFav = useCallback(async (product) => {
    if (!isAuthenticated) return { needLogin: true };
    const pid = product.product_id;
    if (favIds.includes(pid)) { await api.delete(`/me/favourites/${pid}`); setFavIds((f) => f.filter((x) => x !== pid)); }
    else { await api.post(`/me/favourites/${pid}`); setFavIds((f) => [...f, pid]); toast.success("Saved to favourites"); }
    return { needLogin: false };
  }, [isAuthenticated, favIds]);

  return (
    <ShopContext.Provider value={{ basket, refreshBasket, addToBasket, updateQty, removeItem, clearBasket, applyVoucher, removeVoucher, favIds, isFav, toggleFav, refreshFavs }}>
      {children}
    </ShopContext.Provider>
  );
}

export function useShop() {
  const ctx = useContext(ShopContext);
  if (!ctx) throw new Error("useShop must be used within ShopProvider");
  return ctx;
}
